import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { Rights } from '@aiinterview/shared';
import { api } from '../api/client';
import { useSession } from '../api/session';
import { confirmAction } from './ui';
import { DEVELOPER_CREDIT, DEVELOPER_URL } from './credit';

/**
 * The admin shell, reproducing the geometry of the .NET app's _Layout: a 250px white sidebar that
 * owns the brand box and the footer, a 64px topbar, and one scrolling content region. The window
 * itself never scrolls.
 *
 * Navigation is grouped and filtered by rights, but every route behind it is gated on the server as
 * well — a hidden link is not a permission.
 *
 * The labels are the ones the original used, not the engineering names. "Interview formats" rather
 * than "Templates", "Camera rules" rather than "Proctoring": recruiters use this product, and the
 * house rule is that they never see our vocabulary.
 */
interface NavItem {
    to: string;
    label: string;
    right: string;
}

const NAV: { section: string | null; items: NavItem[] }[] = [
    {
        section: null,
        items: [{ to: '/', label: 'Dashboard', right: Rights.View }]
    },
    {
        section: 'Hiring',
        items: [
            { to: '/candidates', label: 'Candidates', right: Rights.View },
            { to: '/interviews', label: 'Interviews', right: Rights.View }
        ]
    },
    {
        section: 'Interactions',
        items: [
            { to: '/surveys', label: 'Surveys', right: Rights.View },
            { to: '/listening', label: 'Listening', right: Rights.View }
        ]
    },
    {
        section: 'Setup',
        items: [
            { to: '/criteria', label: 'What we score', right: Rights.Configure },
            { to: '/questions', label: 'Questions', right: Rights.Configure },
            { to: '/templates', label: 'Interview formats', right: Rights.Configure },
            { to: '/survey-formats', label: 'Survey formats', right: Rights.Configure },
            { to: '/listening-formats', label: 'Listening formats', right: Rights.Configure },
            { to: '/proctoring', label: 'Camera rules', right: Rights.Configure },
            { to: '/settings', label: 'Settings', right: Rights.Configure }
        ]
    },
    {
        section: 'Admin',
        items: [
            { to: '/users', label: 'Team & access', right: Rights.ManageUsers },
            { to: '/integrations', label: 'Other systems', right: Rights.ManageUsers },
            { to: '/audit', label: 'Activity history', right: Rights.ManageUsers }
        ]
    }
];

/**
 * What the topbar calls the current page. Longest path first, so /candidates/import is not matched by
 * /candidates.
 *
 * These must be the words the nav link and the page's own heading use. Three names for one page —
 * "What we score" in the nav, "Criteria" here, "Scoring criteria" as the heading — is how a product
 * stops feeling like one product.
 */
const TITLES: [string, string][] = [
    ['/candidates/import', 'Import from a file'],
    ['/interviews/new', 'New interview'],
    ['/interviews/', 'Interview report'],
    ['/candidates/', 'Candidate'],
    ['/surveys/', 'Survey response'],
    ['/listening/', 'Listening response'],
    ['/proctoring/', 'Camera rules'],
    ['/change-password', 'Change password'],
    ['/candidates', 'Candidates'],
    ['/interviews', 'Interviews'],
    ['/criteria', 'What we score'],
    ['/questions', 'Questions'],
    ['/templates', 'Interview formats'],
    ['/survey-formats', 'Survey formats'],
    ['/listening-formats', 'Listening formats'],
    ['/surveys', 'Surveys'],
    ['/listening', 'Listening sessions'],
    ['/proctoring', 'Camera rules'],
    ['/settings', 'Settings'],
    ['/users', 'Team & access'],
    ['/integrations', 'Other systems'],
    ['/audit', 'Activity history']
];

const titleFor = (pathname: string): string =>
    pathname === '/'
        ? 'Dashboard'
        : TITLES.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? 'Dashboard';

export function Layout({ children }: { children: React.ReactNode }) {
    const { user, branding, can } = useSession();
    const navigate = useNavigate();
    const location = useLocation();

    const [accountOpen, setAccountOpen] = useState(false);
    const accountRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLElement>(null);

    // .app-content is the only scrolling element, so it keeps its offset across route changes and a
    // new page opens halfway down. Every page starts at the top instead.
    useEffect(() => {
        contentRef.current?.scrollTo({ top: 0 });
    }, [location.pathname]);

    // A dropdown that only closes by clicking itself again is a dropdown people leave open.
    useEffect(() => {
        if (!accountOpen) return;

        const close = (event: MouseEvent) => {
            if (!accountRef.current?.contains(event.target as Node)) {
                setAccountOpen(false);
            }
        };

        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [accountOpen]);

    const signOut = async () => {
        const confirmed = await confirmAction({ title: 'Sign out?', confirmText: 'Sign out' });

        if (!confirmed) return;

        await api.post('/api/auth/logout');
        // A full reload rather than a route change: it clears every cached query, so the next person
        // at this machine cannot see the last person's candidate list.
        window.location.assign('/login');
    };

    const productName = branding?.productName ?? 'AI Interview';

    return (
        <div className="app-shell">
            <aside className="app-sidebar">
                <div className="app-brand">
                    {branding?.logoPath || branding?.secondaryLogoPath
                        ? (
                            <span className="brand-logos">
                                {branding.logoPath && <img src={branding.logoPath} alt={productName} />}
                                {/* Decorative: the first logo already names the product, so a second
                                    alt would just be read out twice. */}
                                {branding.secondaryLogoPath && <img src={branding.secondaryLogoPath} alt="" />}
                            </span>
                        )
                        // Falls back to the product name in text when there is no logo, rather than
                        // rendering a broken image.
                        : <span>{productName}</span>}
                </div>

                <nav className="app-nav">
                    {NAV.map((group) => {
                        const visible = group.items.filter((item) => can(item.right));

                        if (visible.length === 0) return null;

                        return (
                            <div key={group.section ?? 'top'}>
                                {group.section && (
                                    <div className="app-nav-section">{group.section}</div>
                                )}
                                {visible.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end={item.to === '/'}
                                        className={({ isActive }) => (isActive ? 'active' : '')}
                                    >
                                        {item.label}
                                    </NavLink>
                                ))}
                            </div>
                        );
                    })}
                </nav>

                <div className="app-footer">
                    <span>{branding?.footerText || productName}</span>
                    {branding?.supportEmail && (
                        <a href={`mailto:${branding.supportEmail}`}>{branding.supportEmail}</a>
                    )}
                    {/* Stacked rather than side by side: 250px has no room for two lines abreast. */}
                    <a
                        className="app-credit"
                        href={DEVELOPER_URL}
                        target="_blank"
                        rel="noreferrer"
                    >{DEVELOPER_CREDIT}</a>
                </div>
            </aside>

            <div className="app-main">
                <header className="app-topbar">
                    <div className="flex items-center gap-3">
                        {/* The topbar names where you are; the page heading below opens the content.
                            The original had both. */}
                        <span className="app-topbar-title">{titleFor(location.pathname)}</span>
                    </div>

                    <div className="app-account" ref={accountRef}>
                        <button
                            type="button"
                            className="app-account-toggle"
                            data-initial={(user?.fullName || user?.userName || '?').charAt(0)}
                            aria-expanded={accountOpen}
                            onClick={() => setAccountOpen((open) => !open)}
                        >
                            {user?.userName}
                            <i
                                className="bi bi-caret-down-fill"
                                style={{ fontSize: '.6rem' }}
                                aria-hidden="true"
                            />
                        </button>

                        {accountOpen && (
                            <div className="app-account-menu">
                                <div className="app-account-role">
                                    {user?.fullName || user?.userName}
                                    {user?.roleCode ? ` · ${user.roleCode}` : ''}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { setAccountOpen(false); navigate('/change-password'); }}
                                >
                                    <i className="bi bi-key" aria-hidden="true" />
                                    Change password
                                </button>
                                <button
                                    type="button"
                                    className="is-danger"
                                    onClick={() => { setAccountOpen(false); void signOut(); }}
                                >
                                    <i className="bi bi-box-arrow-right" aria-hidden="true" />
                                    Sign out
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <main className="app-content" ref={contentRef}>{children}</main>
            </div>
        </div>
    );
}
